(() => {
  const sections = Array.from(document.querySelectorAll('[data-featured-collection-tabs]')).filter((section) => {
    if (section.dataset.featuredCollectionInitialized === 'true') return false;
    section.dataset.featuredCollectionInitialized = 'true';
    return true;
  });

  if (!sections.length) return;

  const productRequests = new Map();
  const productImageSizes = '(min-width: 750px) calc((100vw - 8rem) / 5), 72vw';

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const safeJson = (value) => JSON.stringify(value).replace(/</g, '\\u003c');
  const normalizeEndpoint = (value) => String(value || '').trim().replace(/\/+$/, '');
  const moneyAmount = (money) => {
    const amount = Number(money?.amount ?? money ?? 0);
    return Number.isFinite(amount) ? amount : 0;
  };

  const formatMoney = (amount, currencyCode = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currencyCode
      }).format(amount);
    } catch (error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const normalizeImageUrl = (url) => {
    if (!url) return '';
    return String(url).startsWith('//') ? `${window.location.protocol}${url}` : String(url);
  };

  const withImageWidth = (url, width) => {
    const normalizedUrl = normalizeImageUrl(url);
    if (!normalizedUrl) return '';

    try {
      const parsedUrl = new URL(normalizedUrl, window.location.origin);
      parsedUrl.searchParams.set('width', String(width));
      return parsedUrl.toString();
    } catch (error) {
      return `${normalizedUrl}${normalizedUrl.includes('?') ? '&' : '?'}width=${width}`;
    }
  };

  const buildSrcset = (url) => [320, 540, 760]
    .map((width) => `${withImageWidth(url, width)} ${width}w`)
    .join(', ');

  const getNumericId = (itemOrId) => {
    const id = typeof itemOrId === 'object' ? (itemOrId?.numericId || itemOrId?.id) : itemOrId;
    const match = String(id || '').match(/(\d+)$/);
    return match ? match[1] : String(id || '');
  };

  const getOptionName = (option) => typeof option === 'string' ? option : option?.name || '';
  const getSelectedOptions = (variant) => Array.isArray(variant?.selectedOptions) ? variant.selectedOptions : [];
  const getVariantOptionValue = (variant, optionName) => {
    return getSelectedOptions(variant).find((option) => option?.name === optionName)?.value || '';
  };
  const isVariantAvailable = (variant) => Boolean(variant?.availableForSale || variant?.available);
  const getVisibleVariants = (product) => (product?.variants || []).filter((variant) => !variant.hidden);
  const getSelectedVariant = (product) => {
    const variants = getVisibleVariants(product);
    const selectedId = String(product?.selectedVariantId || '');
    return variants.find((variant) => String(variant.id) === selectedId)
      || variants.find((variant) => getNumericId(variant) === selectedId)
      || variants.find(isVariantAvailable)
      || variants[0]
      || null;
  };

  const getIcon = (root, icon) => {
    return root.querySelector(`template[data-featured-worker-icon="${icon}"]`)?.innerHTML.trim() || '';
  };

  const getProductUrl = (product, variantId = '') => {
    const baseUrl = product?.url || (product?.handle ? `/products/${product.handle}` : '');
    if (!variantId || !baseUrl) return baseUrl;
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}variant=${encodeURIComponent(variantId)}`;
  };

  const renderPrice = (root, product) => {
    const currencyCode = product?.priceRange?.minVariantPrice?.currencyCode
      || root.dataset.featuredCurrencyCode
      || 'USD';
    const price = moneyAmount(product?.priceRange?.minVariantPrice);
    const maxPrice = moneyAmount(product?.priceRange?.maxVariantPrice);
    const compareAtPrice = moneyAmount(product?.compareAtPriceRange?.maxVariantPrice);
    const onSale = compareAtPrice > price;
    const soldOut = !product?.availableForSale;
    const showFrom = maxPrice > price;
    const selectedVariant = (product?.variants || []).find((variant) => moneyAmount(variant?.price) === price)
      || getSelectedVariant(product);
    const productId = product?.numericId || product?.id || '';
    const variantId = getNumericId(selectedVariant);

    return `
      <div class="price${onSale ? ' price--on-sale' : ''}${soldOut ? ' price--sold-out' : ''}" data-price data-product-price data-product-id="${escapeHtml(productId)}" data-variant-id="${escapeHtml(variantId)}" data-base-price-cents="${Math.round(price * 100)}" data-compare-at-price-cents="${Math.round(compareAtPrice * 100)}" data-price-show-from="${showFrom}">
        ${onSale ? `<span class="visually-hidden" data-price-compare-label>Regular price</span><s class="price__compare" data-price-compare>${formatMoney(compareAtPrice, currencyCode)}</s>` : ''}
        <span class="visually-hidden" data-price-current-label>${onSale ? 'Sale price' : 'Regular price'}</span>
        <span class="price__current" data-price-current>
          ${showFrom ? '<span class="price__from">From</span>' : ''}
          ${formatMoney(price, currencyCode)}
        </span>
      </div>
    `;
  };

  const renderImage = (image, title, { className = '' } = {}) => {
    if (!image?.url) return '';
    return `
      <img
        ${className ? `class="${escapeHtml(className)}"` : 'data-product-card-primary-image'}
        data-product-image
        src="${escapeHtml(withImageWidth(image.url, 540))}"
        srcset="${escapeHtml(buildSrcset(image.url))}"
        sizes="${productImageSizes}"
        width="${Number(image.width) || 540}"
        height="${Number(image.height) || 810}"
        alt="${escapeHtml(image.altText || title)}"
        loading="lazy"
        decoding="async"
      >
    `;
  };

  const renderBadges = (product) => {
    const tagLabel = (product?.tags || []).find((tag) => String(tag).startsWith('_label_'));
    const customLabel = product?.badges?.customLabel || (tagLabel ? String(tagLabel).replace('_label_', '') : '');
    if (customLabel) return `<span class="product-card__badge product-card__badge--custom">${escapeHtml(customLabel)}</span>`;
    if (product?.badges?.soldOut || !product?.availableForSale) return '<span class="product-card__badge">Sold out</span>';
    if (product?.badges?.onSale) return `<span class="product-card__badge product-card__badge--sale">${escapeHtml(product.badges.salePercent || 0)}% off</span>`;
    return '';
  };

  const formatSizeLabel = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'small') return 'S';
    if (normalized === 'medium') return 'M';
    if (normalized === 'large') return 'L';
    return value;
  };

  const findSizeVariant = (product, selectedColor, colorOptionName, sizeOptionName, size) => {
    const normalizedColor = String(selectedColor || '').toLowerCase();
    const normalizedSize = String(size || '').toLowerCase();
    return getVisibleVariants(product).find((variant) => {
      if (String(getVariantOptionValue(variant, sizeOptionName)).toLowerCase() !== normalizedSize) return false;
      if (!colorOptionName || !normalizedColor) return true;
      return String(getVariantOptionValue(variant, colorOptionName)).toLowerCase() === normalizedColor;
    }) || null;
  };

  const renderQuickAdd = (root, product) => {
    const selectedVariant = getSelectedVariant(product);
    const title = product?.title || 'Product';
    if (!product?.availableForSale || !selectedVariant) return '<span class="product-card__quick-unavailable">Sold out</span>';

    const options = product?.options || [];
    const colorOptionName = product.colorOptionIndex > -1 ? getOptionName(options[product.colorOptionIndex]) : '';
    const sizeOptionName = product.sizeOptionIndex > -1 ? getOptionName(options[product.sizeOptionIndex]) : '';
    const selectedColor = product.selectedColor || getVariantOptionValue(selectedVariant, colorOptionName);
    const sizes = Array.from(new Set(getVisibleVariants(product)
      .map((variant) => getVariantOptionValue(variant, sizeOptionName))
      .filter(Boolean)));

    if (sizeOptionName && sizes.length) {
      return `
        <details class="product-card__quick-add">
          <summary>${getIcon(root, 'plus')}<span>Quick add</span></summary>
          <div class="product-card__quick-options" aria-label="Quick add ${escapeHtml(title)}">
            ${sizes.map((size) => {
              const variant = findSizeVariant(product, selectedColor, colorOptionName, sizeOptionName, size);
              const available = isVariantAvailable(variant);
              return `
                <form class="product-card__size-form" method="post" action="/cart/add" data-cart-add-form data-product-card-size-form data-product-card-size-value="${escapeHtml(size)}" data-product-card-size-display="${escapeHtml(formatSizeLabel(size))}">
                  <input type="hidden" name="id" value="${escapeHtml(getNumericId(variant))}" data-product-card-variant-input>
                  <input type="hidden" name="quantity" value="1">
                  <button class="product-card__size-button" type="submit" data-product-card-size-button ${available ? '' : 'disabled'} aria-label="${escapeHtml(available ? `Add ${title} - ${size} to bag` : `${title} - ${size} unavailable`)}">${escapeHtml(formatSizeLabel(size))}</button>
                </form>
              `;
            }).join('')}
          </div>
        </details>
      `;
    }

    return `
      <form class="product-card__quick-form" method="post" action="/cart/add" data-cart-add-form>
        <input type="hidden" name="id" value="${escapeHtml(getNumericId(selectedVariant))}" data-product-card-variant-input>
        <input type="hidden" name="quantity" value="1">
        <button class="product-card__quick-submit" type="submit" aria-label="Add ${escapeHtml(title)} to bag">${getIcon(root, 'plus')}<span>Add to cart</span></button>
      </form>
    `;
  };

  const getSwatchStyle = (value) => window.WobSwatches.getSwatchStyle(value);
  const getSwatchLabel = (value) => window.WobSwatches?.getSwatchLabel?.(value) || String(value || '');
  const renderSwatches = (product) => {
    const swatches = window.WobSwatches?.getUniqueSwatches?.(product?.swatches) || [];
    if (swatches.length <= 1) return '';
    const selectedVariant = getSelectedVariant(product);
    const options = product?.options || [];
    const colorOptionName = product.colorOptionIndex > -1 ? getOptionName(options[product.colorOptionIndex]) : '';
    const selectedColor = String(product.selectedColor || getVariantOptionValue(selectedVariant, colorOptionName) || '').toLowerCase();
    const visibleSwatchLimit = 6;

    return `
      <div class="product-card__swatches" aria-label="Color options for ${escapeHtml(product.title)}">
        ${swatches.map((swatch, swatchIndex) => {
          const color = swatch.color || '';
          const image = swatch.image || null;
          const hoverImage = swatch.hoverImage || image;
          const variantId = getNumericId(swatch.variantId);
          const hasImage = swatch.hasColorImage !== false && Boolean(image?.url);
          const isActive = selectedColor && String(color).toLowerCase() === selectedColor;
          return `
            <button
              class="product-card__swatch${isActive ? ' product-card__swatch--active' : ''}"
              type="button"
              data-product-card-swatch
              data-product-card-url="${escapeHtml(swatch.url || getProductUrl(product, variantId))}"
              data-product-card-variant-id="${escapeHtml(variantId)}"
              data-product-card-color-value="${escapeHtml(color)}"
              data-product-card-swatch-product-id="${escapeHtml(product.numericId || product.id || '')}"
              data-product-card-swatch-product-handle="${escapeHtml(product.handle || '')}"
              data-product-card-swatch-product-title="${escapeHtml(product.title || '')}"
              data-swatch-tooltip="${escapeHtml(color)}"
              ${hasImage ? '' : 'data-product-card-image-missing="true"'}
              ${hasImage ? `data-image-src="${escapeHtml(withImageWidth(image.url, 540))}" data-image-srcset="${escapeHtml(buildSrcset(image.url))}" data-image-alt="${escapeHtml(image.altText || product.title)}" data-hover-image-src="${escapeHtml(withImageWidth(hoverImage?.url || image.url, 540))}" data-hover-image-srcset="${escapeHtml(buildSrcset(hoverImage?.url || image.url))}" data-hover-image-alt="${escapeHtml(hoverImage?.altText || product.title)}"` : ''}
              aria-label="${escapeHtml(`${product.title} - ${color}`)}"
              aria-pressed="${isActive ? 'true' : 'false'}"
              ${swatchIndex >= visibleSwatchLimit ? 'hidden' : ''}
            ><span class="product-card__swatch-visual" data-swatch-value="${escapeHtml(color)}" style="${escapeHtml(getSwatchStyle(color))}"></span></button>
          `;
        }).join('')}
        ${swatches.length > visibleSwatchLimit ? `<button class="product-card__swatch-more" type="button" data-product-card-swatches-expand aria-expanded="false" aria-label="Show ${swatches.length - visibleSwatchLimit} more colors for ${escapeHtml(product.title)}">+${swatches.length - visibleSwatchLimit}</button>` : ''}
      </div>
    `;
  };

  const renderSelectedColor = (product) => {
    const swatches = Array.isArray(product?.swatches) ? product.swatches : [];
    if (swatches.length <= 1) return '';
    const variant = getSelectedVariant(product);
    const option = product.colorOptionIndex > -1 ? getOptionName((product.options || [])[product.colorOptionIndex]) : '';
    const color = product.selectedColor || getVariantOptionValue(variant, option);
    return color ? `<p class="product-card__selected-color" data-product-card-selected-color>Color: <span data-product-card-selected-color-text data-swatch-label-raw="${escapeHtml(color)}">${escapeHtml(getSwatchLabel(color))}</span></p>` : '';
  };

  const renderVariantsJson = (product) => safeJson((product?.variants || []).map((variant) => ({
    id: getNumericId(variant),
    available: isVariantAvailable(variant),
    hidden: Boolean(variant.hidden),
    options: getSelectedOptions(variant).map((option) => option.value).filter(Boolean),
    price: Math.round(moneyAmount(variant.price) * 100),
    compare_at_price: Math.round(moneyAmount(variant.compareAtPrice) * 100),
    url: variant.url || getProductUrl(product, getNumericId(variant))
  })));

  const normalizeMediaImage = (image) => image?.url ? {
    url: image.url,
    altText: image.altText || '',
    width: image.width || null,
    height: image.height || null
  } : null;

  const renderMediaJson = (product) => safeJson({
    truncated: Boolean(product?.mediaTruncated),
    images: (product?.images || []).map(normalizeMediaImage).filter(Boolean),
    groups: (product?.mediaGroups || []).map((group) => ({
      color: group.color || '',
      images: (group.images || []).map(normalizeMediaImage).filter(Boolean)
    })).filter((group) => group.images.length)
  });

  const renderProductCard = (root, product, position) => {
    const productId = product.numericId || getNumericId(product.id) || product.handle || position;
    const cardId = `FeaturedWorkerProduct-${productId}-${position}`;
    const productUrl = getProductUrl(product);
    const featuredImage = product.featuredImage || null;
    const secondaryImage = root.dataset.featuredShowSecondaryImage === 'true' ? product.secondaryImage : null;
    const showVendor = root.dataset.featuredShowVendor === 'true' && product.vendor;

    return `
      <article
        class="product-card product-card--${escapeHtml(root.dataset.featuredImageRatio || 'portrait')}"
        data-product-id="${escapeHtml(productId)}"
        data-product-handle="${escapeHtml(product.handle || '')}"
        data-product-title="${escapeHtml(product.title || '')}"
        data-featured-result-position="${position}"
        data-collection-result-position="${position}"
        data-featured-worker-product="true"
        data-product-card-size-option-index="${Number(product.sizeOptionIndex ?? -1)}"
        data-product-card-color-option-index="${Number(product.colorOptionIndex ?? -1)}"
      >
        <div class="product-card__media-shell">
          <a class="product-card__media" href="${escapeHtml(productUrl)}" aria-labelledby="${escapeHtml(cardId)}-title" data-product-card-link>
            ${featuredImage?.url ? renderImage(featuredImage, product.title) : '<div class="product-card__placeholder" aria-hidden="true"></div>'}
            ${secondaryImage?.url && secondaryImage.url !== featuredImage?.url ? renderImage(secondaryImage, product.title, { className: 'product-card__secondary-image' }) : ''}
          </a>
          <div class="product-card__media-controls" data-product-card-carousel-controls hidden>
            <button class="product-card__media-control product-card__media-control--prev" type="button" aria-label="Previous image for ${escapeHtml(product.title)}" data-product-card-carousel-prev>${getIcon(root, 'chevron-left')}</button>
            <button class="product-card__media-control product-card__media-control--next" type="button" aria-label="Next image for ${escapeHtml(product.title)}" data-product-card-carousel-next>${getIcon(root, 'chevron-right')}</button>
          </div>
          <div class="product-card__badges" aria-hidden="true">${renderBadges(product)}</div>
          <div class="product-card__image-actions">${renderQuickAdd(root, product)}</div>
        </div>
        <div class="product-card__content">
          ${showVendor ? `<p class="product-card__vendor">${escapeHtml(product.vendor)}</p>` : ''}
          <h3 class="product-card__title" id="${escapeHtml(cardId)}-title"><a href="${escapeHtml(productUrl)}" data-product-card-link>${escapeHtml(product.title)}</a></h3>
          <div class="product-card__details">${renderPrice(root, product)}</div>
          ${renderSelectedColor(product)}
          ${renderSwatches(product)}
        </div>
        <script type="application/json" data-product-card-variants>${renderVariantsJson(product)}</script>
        <script type="application/json" data-product-card-media>${renderMediaJson(product)}</script>
      </article>
    `;
  };

  const updateCarouselControls = (track) => {
    const carousel = track.closest('.featured-collection__carousel');
    const previousButton = carousel?.querySelector('[data-featured-carousel-prev]');
    const nextButton = carousel?.querySelector('[data-featured-carousel-next]');
    if (!previousButton || !nextButton) return;
    const maxScroll = track.scrollWidth - track.clientWidth - 1;
    previousButton.disabled = track.scrollLeft <= 1;
    nextButton.disabled = track.scrollLeft >= maxScroll;
  };

  const requestProducts = (root, panel) => {
    const endpoint = normalizeEndpoint(root.dataset.featuredWorkerEndpoint);
    const collectionId = panel.dataset.featuredCollectionId || '';
    const collectionHandle = panel.dataset.featuredCollectionHandle || '';
    const sortKey = panel.dataset.featuredSortKey || 'recommended';
    const pageSize = Math.max(4, Math.min(Number(root.dataset.featuredProductsToShow) || 12, 16));
    const key = [endpoint, collectionId, collectionHandle, sortKey, pageSize].join('|');
    if (!endpoint || !collectionHandle || !('fetch' in window)) return Promise.reject(new Error('Featured worker unavailable'));
    if (productRequests.has(key)) return productRequests.get(key);

    const request = fetch(`${endpoint}/collections/products`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        collectionId,
        collectionHandle,
        pageSize,
        sortKey,
        filters: [],
        source: 'homepage-featured-collection'
      })
    }).then(async (response) => {
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok || payload?.fallback?.recommended) {
        throw new Error(payload?.fallback?.reason || 'Featured collection worker unavailable');
      }
      return Array.isArray(payload.products) ? payload.products : [];
    });

    productRequests.set(key, request);
    request.catch(() => productRequests.delete(key));
    return request;
  };

  const revealFallback = (panel) => {
    panel.querySelectorAll('[data-featured-worker-skeleton]').forEach((skeleton) => skeleton.remove());
    panel.classList.remove('is-worker-loading', 'is-worker-ready');
    panel.classList.add('is-worker-fallback');
    panel.querySelector('[data-featured-carousel-track]')?.removeAttribute('aria-busy');
  };

  const loadWorkerPanel = (root, panel) => {
    if (!panel || panel.classList.contains('is-worker-ready') || panel.classList.contains('is-worker-fallback') || panel.classList.contains('is-worker-loading')) return;
    if (
      root.dataset.featuredWorkerEnabled !== 'true'
      || window.WobWorkerMarket?.isPricingCompatible?.() === false
    ) {
      revealFallback(panel);
      return;
    }

    const track = panel.querySelector('[data-featured-carousel-track]');
    if (!track) return;
    panel.classList.add('is-worker-loading');
    track.setAttribute('aria-busy', 'true');

    requestProducts(root, panel).then((products) => {
      if (!products.length) throw new Error('Featured collection returned no worker products');
      track.innerHTML = products.map((product, index) => renderProductCard(root, product, index + 1)).join('');
      panel.classList.remove('is-worker-loading', 'is-worker-fallback');
      panel.classList.add('is-worker-ready');
      track.removeAttribute('aria-busy');
      window.requestAnimationFrame(() => updateCarouselControls(track));
    }).catch(() => revealFallback(panel));
  };

  const getScrollAmount = (track) => {
    const firstCard = track.querySelector('.product-card');
    if (!firstCard) return track.clientWidth;
    const styles = window.getComputedStyle(track);
    return firstCard.getBoundingClientRect().width + (Number.parseFloat(styles.columnGap || styles.gap || '0') || 0);
  };

  const bindCarousel = (track) => {
    const carousel = track.closest('.featured-collection__carousel');
    const previousButton = carousel?.querySelector('[data-featured-carousel-prev]');
    const nextButton = carousel?.querySelector('[data-featured-carousel-next]');
    if (!previousButton || !nextButton) return;
    previousButton.addEventListener('click', () => track.scrollBy({ left: -getScrollAmount(track), behavior: 'smooth' }));
    nextButton.addEventListener('click', () => track.scrollBy({ left: getScrollAmount(track), behavior: 'smooth' }));
    track.addEventListener('scroll', () => updateCarouselControls(track), { passive: true });
    window.addEventListener('resize', () => updateCarouselControls(track));
    updateCarouselControls(track);
  };

  const loadInitialWorkerPanel = (section) => {
    const tabPanels = Array.from(section.querySelectorAll('[role="tabpanel"]'));
    const workerPanels = Array.from(section.querySelectorAll('[data-featured-worker-panel]'));
    const initialPanel = tabPanels.find((panel) => !panel.hidden) || workerPanels[0];
    loadWorkerPanel(section, initialPanel);
  };

  const workerObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      loadInitialWorkerPanel(entry.target);
    });
  }, { rootMargin: '900px 0px' }) : null;

  sections.forEach((section) => {
    const tabs = Array.from(section.querySelectorAll('[data-featured-tab]'));
    const tabPanels = Array.from(section.querySelectorAll('[role="tabpanel"]'));
    const viewAllLink = section.querySelector('[data-featured-view-all]');
    const viewAllLabel = viewAllLink?.querySelector('span');
    const tracks = Array.from(section.querySelectorAll('[data-featured-carousel-track]'));
    tracks.forEach(bindCarousel);

    const activateTab = (activeTab) => {
      const panelId = activeTab.getAttribute('aria-controls');
      let activePanel = null;
      tabs.forEach((tab) => {
        const active = tab === activeTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      tabPanels.forEach((panel) => {
        const active = panel.id === panelId;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        if (active) activePanel = panel;
      });
      if (viewAllLink && activeTab.dataset.featuredViewAllUrl) viewAllLink.href = activeTab.dataset.featuredViewAllUrl;
      if (viewAllLabel && activeTab.dataset.featuredViewAllLabel) {
        viewAllLabel.textContent = activeTab.dataset.featuredViewAllLabel;
      }
      const activeTrack = activePanel?.querySelector('[data-featured-carousel-track]');
      if (activeTrack) activeTrack.scrollLeft = 0;
      loadWorkerPanel(section, activePanel);
      if (activeTrack) window.requestAnimationFrame(() => updateCarouselControls(activeTrack));
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(tab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextTab = tabs[(index + direction + tabs.length) % tabs.length];
        nextTab.focus();
        activateTab(nextTab);
      });
    });

    if (workerObserver) {
      workerObserver.observe(section);
    } else {
      loadInitialWorkerPanel(section);
    }
  });
})();
